import { IBulgeArc, bulgeArc } from '../arc-bulge';

const RAD_TO_DEG: number = 180 / Math.PI;
const QUARTER_TURNS_DEG: readonly number[] = [0, 90, 180, 270];

export type IDxfPoint = readonly [number, number];

export enum EDxfEntityKind {
    Line = 'LINE',
    Polyline = 'LWPOLYLINE',
    Arc = 'ARC',
    Text = 'TEXT',
    Hatch = 'HATCH',
}

/** TEXT group 72 — horizontal justification. */
export enum EDxfHorizontalAlign {
    Left = 0,
    Center = 1,
    Right = 2,
}

/** TEXT group 73 — vertical justification. */
export enum EDxfVerticalAlign {
    Baseline = 0,
    Bottom = 1,
    Middle = 2,
    Top = 3,
}

/** The fill of a HATCH: the 45° pattern of a section or an opening, or a solid fill (an arrowhead). */
export enum EDxfHatchPattern {
    Ansi31 = 'ANSI31',
    Solid = 'SOLID',
}

interface IDxfEntityBase {
    layer: string;
    /**
     * Linetype scale of the entity (group 48): the denominator of the view it belongs to, so a
     * dash measured in paper millimetres prints at its length at the view's scale.
     */
    ltScale: number;
}

export interface IDxfLine extends IDxfEntityBase {
    kind: EDxfEntityKind.Line;
    from: IDxfPoint;
    to: IDxfPoint;
}

export interface IDxfVertex {
    at: IDxfPoint;
    /** Bulge of the segment that starts at this vertex, Y-up: `tan(sweep / 4)`, positive counter-clockwise. */
    bulge: number;
}

export interface IDxfPolyline extends IDxfEntityBase {
    kind: EDxfEntityKind.Polyline;
    vertices: readonly IDxfVertex[];
    closed: boolean;
}

export interface IDxfArc extends IDxfEntityBase {
    kind: EDxfEntityKind.Arc;
    center: IDxfPoint;
    radius: number;
    /** Degrees, counter-clockwise from +x; the arc runs counter-clockwise from start to end. */
    startDeg: number;
    endDeg: number;
}

export interface IDxfText extends IDxfEntityBase {
    kind: EDxfEntityKind.Text;
    at: IDxfPoint;
    height: number;
    text: string;
    rotationDeg: number;
    hAlign: EDxfHorizontalAlign;
    vAlign: EDxfVerticalAlign;
    /** Group 41: how much narrower than the style the text is set to fit its cell; 1 is the style's own. */
    widthFactor: number;
}

export interface IDxfHatch extends IDxfEntityBase {
    kind: EDxfEntityKind.Hatch;
    /** The closed boundary, counter-clockwise or not — a hatch fills it either way. */
    boundary: readonly IDxfPoint[];
    pattern: EDxfHatchPattern;
    /** Group 41: the ANSI31 spacing is 3.175 times this, in drawing units. Unused by a solid fill. */
    patternScale: number;
}

export type IDxfEntity = IDxfLine | IDxfPolyline | IDxfArc | IDxfText | IDxfHatch;

/** An axis-aligned box in millimetres of the file. */
export interface IDxfBounds {
    minX: number;
    minY: number;
    maxX: number;
    maxY: number;
}

/** The box of nothing — the neutral start of a union. */
export const EMPTY_BOUNDS: IDxfBounds = { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity };

export function boundsOfPoints(points: readonly IDxfPoint[]): IDxfBounds {
    return points.reduce(
        (box: IDxfBounds, at: IDxfPoint): IDxfBounds => ({
            minX: Math.min(box.minX, at[0]),
            minY: Math.min(box.minY, at[1]),
            maxX: Math.max(box.maxX, at[0]),
            maxY: Math.max(box.maxY, at[1]),
        }),
        EMPTY_BOUNDS
    );
}

export function unionBounds(a: IDxfBounds, b: IDxfBounds): IDxfBounds {
    return {
        minX: Math.min(a.minX, b.minX),
        minY: Math.min(a.minY, b.minY),
        maxX: Math.max(a.maxX, b.maxX),
        maxY: Math.max(a.maxY, b.maxY),
    };
}

/** A box grown by `by` on every side. */
export function growBounds(box: IDxfBounds, by: number): IDxfBounds {
    return { minX: box.minX - by, minY: box.minY - by, maxX: box.maxX + by, maxY: box.maxY + by };
}

export function isEmptyBounds(box: IDxfBounds): boolean {
    return box.minX > box.maxX || box.minY > box.maxY;
}

/** Whether `angle` lies on the counter-clockwise sweep from `start` to `end`, all in degrees. */
function onSweep(angle: number, start: number, end: number): boolean {
    const span: number = (((end - start) % 360) + 360) % 360 || 360;
    const offset: number = (((angle - start) % 360) + 360) % 360;
    return offset <= span;
}

/** The extreme points of a counter-clockwise arc: its two ends and every quarter point it passes. */
export function arcExtremePoints(center: IDxfPoint, radius: number, startDeg: number, endDeg: number): IDxfPoint[] {
    const at: (deg: number) => IDxfPoint = (deg: number): IDxfPoint => [
        center[0] + radius * Math.cos(deg / RAD_TO_DEG),
        center[1] + radius * Math.sin(deg / RAD_TO_DEG),
    ];
    const quarters: IDxfPoint[] = QUARTER_TURNS_DEG.filter((deg: number): boolean => onSweep(deg, startDeg, endDeg)).map(at);
    return [at(startDeg), at(endDeg), ...quarters];
}

function bulgePoints(from: IDxfPoint, to: IDxfPoint, bulge: number): IDxfPoint[] {
    if (bulge === 0) {
        return [];
    }
    const arc: IBulgeArc = bulgeArc(from, to, bulge);
    // In the file's Y-up frame a positive bulge sweeps counter-clockwise from `from` to `to`.
    const [start, end]: [number, number] =
        bulge > 0 ? [arc.startAngle * RAD_TO_DEG, arc.endAngle * RAD_TO_DEG] : [arc.endAngle * RAD_TO_DEG, arc.startAngle * RAD_TO_DEG];
    return arcExtremePoints(arc.center, arc.radius, start, end);
}

function polylinePoints(entity: IDxfPolyline): IDxfPoint[] {
    const points: IDxfPoint[] = entity.vertices.map((vertex: IDxfVertex): IDxfPoint => vertex.at);
    entity.vertices.forEach((vertex: IDxfVertex, index: number): void => {
        const next: IDxfVertex | undefined = entity.vertices.at(index + 1) ?? (entity.closed ? entity.vertices.at(0) : undefined);
        if (next) {
            points.push(...bulgePoints(vertex.at, next.at, vertex.bulge));
        }
    });
    return points;
}

/** Advances of Arial against the text height, by character class — enough to keep a text inside its cell. */
const NARROW_CHARS: ReadonlySet<string> = new Set<string>(Array.from(' .,:;!|\'"()[]-ilIjtfr1'));
const WIDE_UPPER: ReadonlySet<string> = new Set<string>(Array.from('MWЖМШЩЮЫФ'));
const WIDE_LOWER: ReadonlySet<string> = new Set<string>(Array.from('mwжмшщюыф%'));

function charAdvance(char: string): number {
    if (NARROW_CHARS.has(char)) {
        return 0.3;
    }
    if (WIDE_UPPER.has(char)) {
        return 0.88;
    }
    if (WIDE_LOWER.has(char)) {
        return 0.78;
    }
    if (char !== char.toLowerCase()) {
        return 0.7;
    }
    return char >= '0' && char <= '9' ? 0.56 : 0.57;
}

/**
 * The capital height of Arial against its em. A DXF text height is the capital height (as the
 * ГОСТ 2.304 font size is), so a character's advance in text heights is its em advance over this.
 */
const CAP_HEIGHT_EM: number = 0.716;

/** The estimated width of a text line in Arial; `%%d` counts as the one degree sign it draws. */
export function textWidth(text: string, height: number, widthFactor: number = 1): number {
    const advance: number = Array.from(text.replaceAll('%%d', '°')).reduce(
        (sum: number, char: string): number => sum + charAdvance(char),
        0
    );
    return (advance / CAP_HEIGHT_EM) * height * widthFactor;
}

/** The four corners of a text's estimated box, turned with the text. */
export function textCorners(entity: IDxfText): IDxfPoint[] {
    const width: number = textWidth(entity.text, entity.height, entity.widthFactor);
    const left: number = [0, -width / 2, -width][entity.hAlign];
    const bottom: number = [0, 0, -entity.height / 2, -entity.height][entity.vAlign];
    const angle: number = entity.rotationDeg / RAD_TO_DEG;
    const cos: number = Math.cos(angle);
    const sin: number = Math.sin(angle);
    return [
        [left, bottom],
        [left + width, bottom],
        [left + width, bottom + entity.height],
        [left, bottom + entity.height],
    ].map(([x, y]: number[]): IDxfPoint => [entity.at[0] + x * cos - y * sin, entity.at[1] + x * sin + y * cos]);
}

/** Every point that bounds an entity: ends, vertices, arc extremes, a text's estimated box. */
export function entityPoints(entity: IDxfEntity): IDxfPoint[] {
    switch (entity.kind) {
        case EDxfEntityKind.Line:
            return [entity.from, entity.to];
        case EDxfEntityKind.Polyline:
            return polylinePoints(entity);
        case EDxfEntityKind.Arc:
            return arcExtremePoints(entity.center, entity.radius, entity.startDeg, entity.endDeg);
        case EDxfEntityKind.Text:
            return textCorners(entity);
        case EDxfEntityKind.Hatch:
            return [...entity.boundary];
    }
}

/** The box of a set of entities; `EMPTY_BOUNDS` for none. */
export function entitiesBounds(entities: readonly IDxfEntity[]): IDxfBounds {
    return boundsOfPoints(entities.flatMap(entityPoints));
}

/** The same entity moved by `(dx, dy)` millimetres. */
export function translateEntity(entity: IDxfEntity, dx: number, dy: number): IDxfEntity {
    const move: (point: IDxfPoint) => IDxfPoint = (point: IDxfPoint): IDxfPoint => [point[0] + dx, point[1] + dy];
    switch (entity.kind) {
        case EDxfEntityKind.Line:
            return { ...entity, from: move(entity.from), to: move(entity.to) };
        case EDxfEntityKind.Polyline:
            return { ...entity, vertices: entity.vertices.map((vertex: IDxfVertex): IDxfVertex => ({ ...vertex, at: move(vertex.at) })) };
        case EDxfEntityKind.Arc:
            return { ...entity, center: move(entity.center) };
        case EDxfEntityKind.Text:
            return { ...entity, at: move(entity.at) };
        case EDxfEntityKind.Hatch:
            return { ...entity, boundary: entity.boundary.map(move) };
    }
}

export function translateBounds(box: IDxfBounds, dx: number, dy: number): IDxfBounds {
    return { minX: box.minX + dx, minY: box.minY + dy, maxX: box.maxX + dx, maxY: box.maxY + dy };
}
